<script setup>
import TicketsDataTable from "@/components/helpdesk/tickets/TicketsDataTable.vue";
import { onMounted, ref, watch, computed } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import DeleteConfirm from "@/components/common/DeleteConfirm.vue";
const $loading = useLoading(loaderData);
import { useRouter, useRoute } from "vue-router";
import TicketFilters from "@/components/helpdesk/tickets/TicketFilters.vue";
const router = useRouter();
const route = useRoute();

const user = JSON.parse(localStorage.getItem('userData'));

const errors = ref([]);
const headers = ref([
  {
    title: "Ticket ID",
    key: "ticket_id",
    sortable: true,
  },
  {
    title: "Status",
    key: "status",
    sortable: true,
  },
  {
    title: "Description",
    key: "description",
    sortable: false,
  },
  {
    title: "Form",
    key: "form",
    sortable: true,
  },
  {
    title: "Created At",
    key: "date",
    sortable: true,
  },
  {
    title: "Created By",
    key: "submitted_by",
    sortable: true,
  },
  {
    title: "",
    key: "actions",
    sortable: false,
  },
]);

const ticketStatusList = [
  {
    title: "Submitted",
    value: "submitted",
  },
  {
    title: "Awaiting Response",
    value: "awaiting_response",
  },
  {
    title: "Closed",
    value: "closed",
  },
];
const assignedToList = ref([]);

const tickets = ref([]);
const ticketStats = ref({
  total_tickets: 0,
  tickets_submitted_today: 0,
  tickets_responded_today: 0,
});

const openModal = ref(false);
const deleteDialog = ref(false);
const modeModal = ref("create");
const currentTab = ref("allTickets");
const filterInfo = ref({});
const editedItem = ref({});

const openCreateModel = async () => {
  modeModal.value = "create";
  editedItem.value = {};
  openModal.value = true;
};

const getHelpdeskTickets = async (payload = null) => {
  let url = `/helpdesk/tickets?page=${payload.page}&per_page=${
    payload.perPage
  }&list=${payload.list ? payload.list : "all"}`;
  if (payload.searchQuery && payload.searchQuery.trim() !== "") {
    url += `&search_query=${encodeURIComponent(payload.searchQuery.trim())}`;
  }
  url += `&sort_by=${payload?.sortBy ? payload.sortBy : 'date'}&order_by=${payload?.orderBy ? payload.orderBy : 'desc'}`;
  if (payload.status) {
    url += `&status=${payload.status}`;
  }
  if (Array.isArray(payload.assigned_to) && payload.assigned_to.length > 0) {
    const assignedToQuery = payload.assigned_to
      .map(value => `assigned_to[]=${encodeURIComponent(value)}`)
      .join("&");

    url += `&${assignedToQuery}`;
  }
  let loader = $loading.show();
  axios
    .get(url)
    .then((response) => {
      if (response.status == "success") {
        tickets.value = response.data.tickets;
        ticketStats.value = response.data.stats || {
          total_tickets: 0,
          tickets_submitted_today: 0,
          tickets_responded_today: 0,
        };
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      loader.hide();
    })
    .finally(() => {
      loader.hide();
    });
};

// Page changed from pagination
const tableChanged = async (payload) => {
  filterInfo.value = { ...filterInfo.value, ...payload };
  getHelpdeskTickets(filterInfo.value);
};

const searchValue = async (payload) => {
  filterInfo.value.searchQuery = payload;
  getHelpdeskTickets(filterInfo.value);
};

const filterListChanged = async (payload) => {
  filterInfo.value.list = payload;
  getHelpdeskTickets(filterInfo.value);
};
const statusChanged = async payload => {
  filterInfo.value.status = payload
  getHelpdeskTickets(filterInfo.value)
}
const assignedToChanged = async payload => {
  filterInfo.value.assigned_to = Array.isArray(payload) ? payload : []
  getHelpdeskTickets(filterInfo.value)
}

const deleteItemConfirm = async () => {
  axios
    .delete("/helpdesk/tickets/" + `${editedItem.value.id}`)
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 2000,
        });
        deleteDialog.value = false;
        // refresh the users list via API
        getHelpdeskTickets(filterInfo.value);
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

// Fetch users, teams, and roles for assigned_to filter
const fetchAssignedToOptions = async () => {
  try {
    const [usersResponse] = await Promise.all([
      axios.get('/get-users-for-tickets'),
    ]);

    const options = [];

    // Add users
    if (usersResponse?.data?.users) {
      usersResponse.data.users.forEach(user => {
        options.push({
          title: `${user.first_name} ${user.last_name}`,
          value: user.id,
          type: 'user'
        });
      });
    }

    assignedToList.value = options;
  } catch (error) {
    console.error('Error fetching assigned_to options:', error);
  }
};

// load contacts when page loads
onMounted(async () => {
  let valueToInsert = {
    title: "Assigned To",
    key: "assigned_to",
    sortable: false,
  };

  if (user?.role == "super_admin") {
    headers.value.splice(2, 0, valueToInsert);
  }

  // Fetch assigned_to filter options
  await fetchAssignedToOptions();

  const tabFromQuery = String(route.query.tab || '').toLowerCase();
  if (['all', 'assigned', 'my'].includes(tabFromQuery)) {
    currentTab.value = tabFromQuery;
  }
});

watch(
  () => currentTab.value,
  (newValue, oldValue) => {
    filterListChanged(newValue);
  }
);

// expose errors and getContacts
defineExpose({
  errors,
  getHelpdeskTickets,
});

const viewTicket = (item) => {
  router.push({
    name: "helpdesk-ticket-id",
    params: { id: item.id },
  });
};
const deleteTicket = (item) => {
  editedItem.value = item;
  deleteDialog.value = true;
};

const ticketRows = computed(() => {
  if (Array.isArray(tickets.value?.data)) return tickets.value.data;
  if (Array.isArray(tickets.value)) return tickets.value;
  return [];
});

const totalTicketsCount = computed(() => {
  if (typeof ticketStats.value?.total_tickets === 'number') return ticketStats.value.total_tickets;
  if (typeof tickets.value?.total === 'number') return tickets.value.total;
  return ticketRows.value.length;
});

const parseTicketDate = (rawDate) => {
  if (!rawDate) return null;
  const parsed = new Date(rawDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isToday = (rawDate) => {
  const parsed = parseTicketDate(rawDate);
  if (!parsed) return false;

  const now = new Date();
  return parsed.getFullYear() === now.getFullYear()
    && parsed.getMonth() === now.getMonth()
    && parsed.getDate() === now.getDate();
};

const getFirstAvailableDate = (ticket, keys = []) => {
  for (const key of keys) {
    if (ticket?.[key]) return ticket[key];
  }
  return null;
};

const ticketsSubmittedTodayCount = computed(() => {
  if (typeof ticketStats.value?.tickets_submitted_today === 'number') {
    return ticketStats.value.tickets_submitted_today;
  }

  return ticketRows.value.filter(ticket => {
    const status = String(ticket?.status || '').toLowerCase();
    const submittedToday = status === 'submitted' && isToday(
      getFirstAvailableDate(ticket, ['updated_at']),
    );

    return submittedToday;
  }).length;
});

const ticketsRespondedTodayCount = computed(() => {
  if (typeof ticketStats.value?.tickets_responded_today === 'number') {
    return ticketStats.value.tickets_responded_today;
  }

  return ticketRows.value.filter(ticket => {
    const status = String(ticket?.status || '').toLowerCase();

    if (status === 'awaiting_response') return false;

    const closedToday = status === 'closed' && isToday(
      getFirstAvailableDate(ticket, ['updated_at']),
    );

    return closedToday;
  }).length;
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>

  <VTabs v-model="currentTab" class="v-tabs-pill mb-2">
    <VTab key="all" value="all">All Tickets</VTab>
    <VTab key="assigned" value="assigned">Assigned To Me</VTab>
    <VTab key="my" value="my">My Tickets</VTab>
  </VTabs>

  <VRow class="mb-2">
    <VCol cols="12" md="4">
      <VCard variant="tonal" color="primary">
        <VCardText class="d-flex align-center justify-space-between">
          <div>
            <div class="text-caption text-medium-emphasis">Total Tickets</div>
            <div class="text-h5 font-weight-bold">{{ totalTicketsCount }}</div>
          </div>
          <VIcon icon="tabler-ticket" size="28" />
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="4">
      <VCard variant="tonal" color="success">
        <VCardText class="d-flex align-center justify-space-between">
          <div>
            <div class="text-caption text-medium-emphasis">Tickets Submitted Today</div>
            <div class="text-h5 font-weight-bold">{{ ticketsSubmittedTodayCount }}</div>
          </div>
          <VIcon icon="tabler-calendar-plus" size="28" />
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="4">
      <VCard variant="tonal" color="info">
        <VCardText class="d-flex align-center justify-space-between">
          <div>
            <div class="text-caption text-medium-emphasis">Tickets Responded Today</div>
            <div class="text-h5 font-weight-bold">{{ ticketsRespondedTodayCount }}</div>
          </div>
          <VIcon icon="tabler-message-circle" size="28" />
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <VCard>

    <VCardText class="p-0" style="padding: 0 !important">
      <TicketFilters
        :width="4"
        :status="ticketStatusList"
        @status-changed="statusChanged"
        :assignedTo="assignedToList"
        @assigned-to-changed="assignedToChanged"
      />
      <VDivider />
      <TicketsDataTable
        :headers="headers"
        :table-data="tickets"
        @tableChanged="tableChanged"
        @searchValue="searchValue"
        @isDialogVisible="openCreateModel"
        @filterListChanged="filterListChanged"
        @viewTicket="viewTicket"
        @deleteTicket="deleteTicket"
        :user="user"
      />

    </VCardText>

  </VCard>

  <DeleteConfirm
    v-model:deleteDialog="deleteDialog"
    @closeDelete="deleteDialog = false"
    @deleteItemConfirm="deleteItemConfirm"
  />
</template>
